// ระบบ Level/Reputation สำหรับ Profile และ Store
// ใช้เพื่อความน่าเชื่อถือในระบบตลาดค้าส่ง

/**
 * กำหนด Level และคะแนน
 */
export const LEVELS = {
  1: { name: 'Bronze', minScore: 0, maxScore: 100, color: '#CD7F32' },
  2: { name: 'Silver', minScore: 101, maxScore: 500, color: '#C0C0C0' },
  3: { name: 'Gold', minScore: 501, maxScore: 2000, color: '#FFD700' },
  4: { name: 'Platinum', minScore: 2001, maxScore: 5000, color: '#E5E4E2' },
  5: { name: 'Diamond', minScore: 5001, maxScore: Infinity, color: '#B9F2FF' }
};

/**
 * คำนวณ Level จากคะแนน
 * @param {number} score - คะแนนรวม
 * @returns {number} - Level (1-5)
 */
export const calculateLevel = (score) => {
  if (score >= 5001) return 5;
  if (score >= 2001) return 4;
  if (score >= 501) return 3;
  if (score >= 101) return 2;
  return 1;
};

/**
 * ดึงข้อมูล Level
 * @param {number} level - Level (1-5)
 * @returns {object} - ข้อมูล Level
 */
export const getLevelInfo = (level) => {
  return LEVELS[level] || LEVELS[1];
};

/**
 * คำนวณคะแนน Profile
 * @param {object} profileData - ข้อมูล Profile
 * @returns {number} - คะแนนรวม
 */
export const calculateProfileScore = (profileData) => {
  let score = 0;
  
  // คะแนนจากออเดอร์สำเร็จ
  const completedOrders = profileData.completedOrders || 0;
  score += completedOrders * 10;
  
  // คะแนนจากรีวิว
  const averageRating = profileData.averageRating || 0;
  const totalReviews = profileData.totalReviews || 0;
  if (averageRating >= 4.5) {
    score += totalReviews * 5;
  } else if (averageRating >= 4.0) {
    score += totalReviews * 3;
  } else if (averageRating >= 3.5) {
    score += totalReviews * 1;
  }
  
  // คะแนนจากรีวิวลบ
  const negativeReviews = profileData.negativeReviews || 0;
  score -= negativeReviews * 5;
  
  // คะแนนจากเวลาที่ใช้ในระบบ (เดือน)
  const monthsInSystem = profileData.monthsInSystem || 0;
  score += monthsInSystem * 2;
  
  // คะแนนจากออเดอร์ที่ส่งของตรงเวลา
  const onTimeDeliveries = profileData.onTimeDeliveries || 0;
  score += onTimeDeliveries * 3;
  
  return Math.max(0, score); // ไม่ให้คะแนนติดลบ
};

/**
 * คำนวณคะแนน Store
 * @param {object} storeData - ข้อมูล Store
 * @returns {number} - คะแนนรวม
 */
export const calculateStoreScore = (storeData) => {
  let score = 0;
  
  // คะแนนจากจำนวนสินค้าที่ขาย
  const totalProductsSold = storeData.totalProductsSold || 0;
  score += totalProductsSold * 2;
  
  // คะแนนจากออเดอร์สำเร็จ
  const completedOrders = storeData.completedOrders || 0;
  score += completedOrders * 10;
  
  // คะแนนจากรีวิวร้าน
  const averageRating = storeData.averageRating || 0;
  const totalReviews = storeData.totalReviews || 0;
  if (averageRating >= 4.5) {
    score += totalReviews * 5;
  } else if (averageRating >= 4.0) {
    score += totalReviews * 3;
  } else if (averageRating >= 3.5) {
    score += totalReviews * 1;
  }
  
  // คะแนนจากรีวิวลบ
  const negativeReviews = storeData.negativeReviews || 0;
  score -= negativeReviews * 5;
  
  // คะแนนจากอัตราการส่งของตรงเวลา
  const onTimeDeliveryRate = storeData.onTimeDeliveryRate || 0;
  if (onTimeDeliveryRate >= 0.95) {
    score += 50;
  } else if (onTimeDeliveryRate >= 0.90) {
    score += 30;
  } else if (onTimeDeliveryRate >= 0.80) {
    score += 10;
  }
  
  // คะแนนจากเวลาที่เปิดร้าน (เดือน)
  const monthsOpen = storeData.monthsOpen || 0;
  score += monthsOpen * 3;
  
  return Math.max(0, score); // ไม่ให้คะแนนติดลบ
};

/**
 * อัพเดทคะแนน Profile
 * @param {string} userId - ID ของ user
 * @param {object} updates - ข้อมูลที่อัพเดท
 * @returns {Promise<object>} - ข้อมูล Level และคะแนนใหม่
 */
export const updateProfileScore = async (userId, updates) => {
  try {
    const { doc, getDoc, updateDoc } = await import('firebase/firestore');
    const { db } = await import('../firebase/firebaseConfig');
    
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      throw new Error('User not found');
    }
    
    const currentData = userSnap.data();
    const profileData = {
      ...currentData,
      ...updates,
      updatedAt: new Date()
    };
    
    // คำนวณคะแนนใหม่
    const newScore = calculateProfileScore(profileData);
    const newLevel = calculateLevel(newScore);
    
    // อัพเดทข้อมูล
    await updateDoc(userRef, {
      reputationScore: newScore,
      reputationLevel: newLevel,
      ...updates,
      updatedAt: new Date()
    });
    
    return {
      score: newScore,
      level: newLevel,
      levelInfo: getLevelInfo(newLevel)
    };
  } catch (error) {
    console.error('Error updating profile score:', error);
    throw error;
  }
};

/**
 * อัพเดทคะแนน Store
 * @param {string} storeId - ID ของ store
 * @param {object} updates - ข้อมูลที่อัพเดท
 * @returns {Promise<object>} - ข้อมูล Level และคะแนนใหม่
 */
export const updateStoreScore = async (storeId, updates) => {
  try {
    const { doc, getDoc, updateDoc } = await import('firebase/firestore');
    const { db } = await import('../firebase/firebaseConfig');
    
    const storeRef = doc(db, 'stores', storeId);
    const storeSnap = await getDoc(storeRef);
    
    if (!storeSnap.exists()) {
      throw new Error('Store not found');
    }
    
    const currentData = storeSnap.data();
    const storeData = {
      ...currentData,
      ...updates,
      updatedAt: new Date()
    };
    
    // คำนวณคะแนนใหม่
    const newScore = calculateStoreScore(storeData);
    const newLevel = calculateLevel(newScore);
    
    // อัพเดทข้อมูล
    await updateDoc(storeRef, {
      reputationScore: newScore,
      reputationLevel: newLevel,
      ...updates,
      updatedAt: new Date()
    });
    
    return {
      score: newScore,
      level: newLevel,
      levelInfo: getLevelInfo(newLevel)
    };
  } catch (error) {
    console.error('Error updating store score:', error);
    throw error;
  }
};

/**
 * เพิ่มคะแนนเมื่อออเดอร์สำเร็จ
 * @param {string} userId - ID ของ user
 * @param {string} storeId - ID ของ store (ถ้ามี)
 * @returns {Promise<void>}
 */
export const addScoreOnOrderComplete = async (userId, storeId = null) => {
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('../firebase/firebaseConfig');
    
    // อัพเดท Profile Score
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      const currentCompletedOrders = userData.completedOrders || 0;
      
      await updateProfileScore(userId, {
        completedOrders: currentCompletedOrders + 1
      });
    }
    
    // อัพเดท Store Score (ถ้ามี)
    if (storeId) {
      const storeRef = doc(db, 'stores', storeId);
      const storeSnap = await getDoc(storeRef);
      
      if (storeSnap.exists()) {
        const storeData = storeSnap.data();
        const currentCompletedOrders = storeData.completedOrders || 0;
        
        await updateStoreScore(storeId, {
          completedOrders: currentCompletedOrders + 1
        });
      }
    }
  } catch (error) {
    console.error('Error adding score on order complete:', error);
  }
};

/**
 * เพิ่มคะแนนเมื่อส่งของตรงเวลา
 * @param {string} userId - ID ของ user
 * @param {string} storeId - ID ของ store (ถ้ามี)
 * @returns {Promise<void>}
 */
export const addScoreOnTimeDelivery = async (userId, storeId = null) => {
  try {
    // อัพเดท Profile Score
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('../firebase/firebaseConfig');
    
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      const currentOnTimeDeliveries = userData.onTimeDeliveries || 0;
      
      await updateProfileScore(userId, {
        onTimeDeliveries: currentOnTimeDeliveries + 1
      });
    }
    
    // อัพเดท Store Score (ถ้ามี)
    if (storeId) {
      const storeRef = doc(db, 'stores', storeId);
      const storeSnap = await getDoc(storeRef);
      
      if (storeSnap.exists()) {
        const storeData = storeSnap.data();
        const currentOnTimeDeliveries = storeData.onTimeDeliveries || 0;
        const totalDeliveries = storeData.totalDeliveries || 0;
        const onTimeDeliveryRate = (currentOnTimeDeliveries + 1) / (totalDeliveries + 1);
        
        await updateStoreScore(storeId, {
          onTimeDeliveries: currentOnTimeDeliveries + 1,
          totalDeliveries: totalDeliveries + 1,
          onTimeDeliveryRate: onTimeDeliveryRate
        });
      }
    }
  } catch (error) {
    console.error('Error adding score on time delivery:', error);
  }
};


